import { faCodeMerge, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { formatShortDateTime } from '@/utils/dates';
import formatPersonName from '@/utils/formatPersonName';

import type { Props } from './types';

const PersonDeletedNotice = ({ person }: Props): JSX.Element => {
  const name = formatPersonName({ person, withPronouns: false }) || 'This record';
  const mergedInto = person.merged_into;
  const deletedOn = person.deleted_at ? formatShortDateTime(person.deleted_at) : null;

  const mergedName = mergedInto
    ? [mergedInto.first_name, mergedInto.last_name].filter(Boolean).join(' ')
    : '';
  // Fall back to a name-agnostic label when the surviving record has no name,
  // so the CTA doesn't read "View the surviving profile's profile".
  const mergedLinkLabel = mergedName
    ? `View ${mergedName}'s profile`
    : 'View the surviving profile';

  return (
    <div className="mx-auto mt-8 max-w-2xl px-4">
      <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-50 text-yellow-600 dark:bg-yellow-950/40 dark:text-yellow-400">
          <FontAwesomeIcon icon={mergedInto ? faCodeMerge : faUserSlash} className="h-5 w-5" />
        </div>

        {mergedInto ? (
          <>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              This record was merged
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="mp-sensitive">{name}</span> was merged into another profile
              {deletedOn ? ` on ${deletedOn}` : ''}. All of this person&apos;s information now lives
              on the surviving record.
            </p>
            <Link
              to={`/people/${mergedInto.id}`}
              className="mt-6 inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
              <span className={mergedName ? 'mp-sensitive' : undefined}>{mergedLinkLabel}</span>
            </Link>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              This record was deleted
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="mp-sensitive">{name}</span> was deleted
              {deletedOn ? ` on ${deletedOn}` : ''}. This profile is kept for historical reference
              and can no longer be edited.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PersonDeletedNotice;
